import { consoleRoutes, type WorkflowKind } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Badge, Button, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger, Icon, Input, StatusBadge, Tooltip, type StatusKind } from "@sagot/ui";
import type { Ref } from "react";
import { useWorkspaceContext } from "../../../session/workspace-context";
import { DefaultBadge, ModifiedBadge, UpdateAvailableBadge } from "./defaults/default-badges";
import { DefaultHeaderMenu, type DefaultActions } from "./defaults/default-header-menu";
import { KindBadge } from "./kind-badge";
import type { WorkflowDetail } from "./types";

const B = "engage.workflows.builder";
/** The note that says why Publish is off for someone who may edit but not publish (rendered by the page). */
export const PUBLISH_HINT_ID = "workflow-publish-hint";
/** Says how many problems stand between the draft and Publish (the server still checks when it is published). */
export const PUBLISH_PROBLEMS_ID = "workflow-publish-problems";
export const STATE_STATUS: Record<string, StatusKind> = { live: "success", paused: "neutral", draft: "warning" };

interface WorkflowHeaderProps {
  workflow: WorkflowDetail;
  /** The kind of the definition being edited (it changes as customer-facing steps are added). */
  kind: WorkflowKind;
  name: string;
  dirty: boolean;
  canManage: boolean;
  canPublish: boolean;
  /** The member may try the draft (Preview for customer-facing workflows, the dry run for the rest). */
  canPreview: boolean;
  busy: boolean;
  /** Problems found in the draft: they are named on the Publish button. */
  problemCount?: number;
  onName: (name: string) => void;
  onPreview: () => void;
  onSave: () => void;
  onPublish: () => void;
  onPause: () => void;
  onResume: () => void;
  onDelete: () => void;
  onTranslations: () => void;
  /** The More button: the dialogs its menu opens give focus back to it when they close. */
  moreRef?: Ref<HTMLButtonElement>;
  /** What the menu does for a default workflow (Duplicate, Restore default, Compare); a workflow of the workspace's own has none. */
  defaults?: DefaultActions;
}

/** Name, state, version and the actions (save, publish, pause / resume, delete). */
export function WorkflowHeader({ workflow, kind, name, dirty, canManage, canPublish, canPreview, busy, problemCount = 0, onName, onPreview, onSave, onPublish, onPause, onResume, onDelete, onTranslations, moreRef, defaults }: WorkflowHeaderProps) {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  const live = workflow.state === "live";
  /** A draft that was never published is "Draft" already: "Unpublished changes" would read as if something live had changed (the list page says the same). */
  const neverPublished = workflow.state === "draft" && !workflow.live_version;
  // Publish stays available with problems (the server checks when it is published); it says how many there are.
  // While a save or publish runs both buttons are off: a second save would go out with the old base version and be refused as "changed elsewhere".
  const publishHint = t("engage.workflows.extras.publishHint", { count: problemCount });
  const publish = (
    <Button
      loading={busy}
      disabled={!canPublish || busy || (!dirty && !workflow.has_unpublished_changes && live)}
      aria-describedby={canPublish ? (problemCount > 0 ? PUBLISH_PROBLEMS_ID : undefined) : PUBLISH_HINT_ID}
      data-problems={problemCount > 0 ? String(problemCount) : undefined}
      onClick={onPublish}
    >
      <Icon name="play" />
      {t(`${B}.publish`)}
    </Button>
  );
  return (
    <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
      <div className="flex min-w-0 items-start gap-2">
        <Button asChild variant="ghost" size="sm" iconOnly aria-label={t(`${B}.back`)}>
          <AppLink href={consoleRoutes.workflows(summary.slug)}>
            <Icon name="arrow-left" />
          </AppLink>
        </Button>
        <div className="min-w-0">
          <h1 className="sr-only">{t(`${B}.heading`)}</h1>
          <div className="flex flex-wrap items-center gap-2">
            <label htmlFor="workflow-name" className="sr-only">{t("engage.workflows.create.name")}</label>
            <Input id="workflow-name" className="h-9 w-full min-w-0 text-lg font-extrabold sm:w-80" maxLength={120} disabled={!canManage} value={name} onChange={(e) => { onName(e.target.value); }} />
            <StatusBadge status={STATE_STATUS[workflow.state] ?? "neutral"} label={t(`engage.workflows.states.${workflow.state}`, { defaultValue: workflow.state })} />
            <KindBadge kind={kind} />
            {defaults ? <DefaultBadge /> : null}
            {defaults && workflow.is_modified ? <ModifiedBadge /> : null}
            {defaults && workflow.has_default_update ? <UpdateAvailableBadge /> : null}
            {workflow.live_version ? <Badge className="font-mono">{t(`${B}.version`, { version: workflow.live_version })}</Badge> : null}
            {(workflow.has_unpublished_changes && !neverPublished) || dirty ? <Badge tone="warning">{t("engage.workflows.unpublished")}</Badge> : null}
          </div>
          <p className="mt-1 text-xs text-fg-subtle">{t(`engage.workflows.triggers.${workflow.trigger}`, { defaultValue: workflow.trigger })}</p>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {canPreview ? (
          <Button variant="secondary" onClick={onPreview}>
            <Icon name="eye" />
            {t(`${B}.preview.open`)}
          </Button>
        ) : null}
        {canManage ? (
          <Button variant="secondary" loading={busy} disabled={busy || !dirty} onClick={onSave}>
            <Icon name="check" />
            {t(`${B}.save`)}
          </Button>
        ) : null}
        {canManage ? (
          <>
            {problemCount > 0 ? <Tooltip content={publishHint}>{publish}</Tooltip> : publish}
            {problemCount > 0 ? <span id={PUBLISH_PROBLEMS_ID} className="sr-only">{publishHint}</span> : null}
          </>
        ) : null}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button ref={moreRef} variant="ghost" iconOnly aria-label={t(`${B}.more`)}>
              <Icon name="more-horizontal" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            {canPublish && live ? (
              <DropdownMenuItem onSelect={onPause}>
                <Icon name="pause" />
                {t(`${B}.pause`)}
              </DropdownMenuItem>
            ) : null}
            {canPublish && workflow.state === "paused" && workflow.live_version ? (
              <DropdownMenuItem onSelect={onResume}>
                <Icon name="play" />
                {t(`${B}.resume`)}
              </DropdownMenuItem>
            ) : null}
            {defaults ? <DefaultHeaderMenu canManage={canManage} canPublish={canPublish} workflow={workflow} {...defaults} /> : null}
            {defaults ? null : (
              <DropdownMenuItem onSelect={onTranslations}>
                <Icon name="languages" />
                {t(`${B}.tabs.translations`)}
              </DropdownMenuItem>
            )}
            {canManage && !defaults ? (
              <>
                <DropdownMenuSeparator />
                {live ? (
                  <DropdownMenuItem disabled>
                    <Icon name="trash" />
                    {t(`${B}.deleteLive`)}
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem tone="danger" onSelect={onDelete}>
                    <Icon name="trash" />
                    {t(`${B}.delete`)}
                  </DropdownMenuItem>
                )}
              </>
            ) : null}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}
